import { Link } from "react-router-dom";
import { Briefcase, Lightbulb, ShieldCheck, Heart, ArrowRight } from "lucide-react";
import MarketingShell from "@/components/marketing/MarketingShell";
import SEO from "@/components/marketing/SEO";

const VALUES = [
  { icon: Briefcase, title: "Built by a CA", body: "TAXINPHO is built by a practising Chartered Accountant who lives the daily reality of GST/ITR/TDS filings. Every feature solves a real practice problem, not a hypothetical one." },
  { icon: Lightbulb, title: "AI that helps, not hypes", body: "We use GPT-5.2 carefully — for HSN lookup, message drafting, return summaries — never to replace your professional judgement or compliance accuracy." },
  { icon: ShieldCheck, title: "Data privacy first", body: "Your client data never leaves your account. We don't train AI on your data. We don't sell anything to anyone. We're a SaaS, not an ad business." },
  { icon: Heart, title: "Made in India 🇮🇳", body: "Designed for Indian compliance — GSTIN, PAN, HSN, IFSC, INR, GST/CGST/SGST/IGST. Hosted on Indian cloud. Made by Indians for Indians." },
];

export default function About() {
  return (
    <MarketingShell>
      <SEO
        title="About TAXINPHO — Built by a CA, for Indian CAs"
        description="TAXINPHO is a tax-services provider based in Balangir, Odisha. Owned by Nirmala Agrawal (Proprietor) and managed by Adv Mrinal Agrawal. Built for Chartered Accountants who deserve better software."
        canonical="https://taxinpho.co/about"
      />
      <section className="max-w-4xl mx-auto px-5 py-16 md:py-24">
        <p className="text-xs font-bold uppercase tracking-widest text-[#002FA7] mb-3">About</p>
        <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tighter mb-6">
          We&apos;re fixing<br />the CA office.
        </h1>
        <p className="text-zinc-600 text-lg leading-relaxed mb-10">
          TAXINPHO is a tax-services provider based in
          <strong> Balangir, Odisha</strong>. Owned by <strong>Nirmala Agrawal</strong> as
          proprietor and managed by <strong>Adv Mrinal Agrawal</strong>, we built
          TAXINPHO because no existing tool gave us a single screen for clients,
          returns, invoices, accounting and AI — without stitching together 7 different apps.
        </p>

        <div className="bg-zinc-50 border border-zinc-200 p-8 mb-6">
          <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-3">Proprietor &amp; Owner</p>
          <h2 className="font-display font-bold text-3xl tracking-tight mb-3">Nirmala Agrawal</h2>
          <p className="text-zinc-700 leading-relaxed text-sm">
            Sole proprietor and legal owner of TAXINPHO. Holder of Trade License
            No. <span className="font-mono text-xs bg-zinc-100 px-1.5 py-0.5">TL/BLR/2023-07-14/033462</span>
            issued by Balangir Municipality, Odisha, under Section 617 of OMC &amp;
            Section 337 of OM Act — valid through 6 December 2026.
          </p>
        </div>

        <div className="bg-zinc-50 border border-zinc-200 p-8 mb-12">
          <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-3">Managing &amp; Founder</p>
          <h2 className="font-display font-bold text-3xl tracking-tight mb-3">Adv Mrinal Agrawal</h2>
          <p className="text-zinc-700 leading-relaxed text-sm">
            Advocate and Founder of TAXINPHO. Mrinal manages day-to-day operations
            and has built TAXINPHO out of the daily frustration of switching between
            Excel, Tally, GST portal, ITR utility, WhatsApp, and email — multiple
            times per client. TAXINPHO is the tool he wishes existed on day one.
          </p>
        </div>

        <h2 className="font-display font-black text-3xl tracking-tighter mb-8">
          What we believe.
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-14">
          {VALUES.map((v) => (
            <div key={v.title} className="border border-zinc-200 p-6">
              <v.icon size={24} className="text-[#002FA7] mb-3" />
              <h3 className="font-display font-bold text-lg mb-2">{v.title}</h3>
              <p className="text-sm text-zinc-600 leading-relaxed">{v.body}</p>
            </div>
          ))}
        </div>

        <div className="bg-zinc-950 text-white p-10">
          <h3 className="font-display font-black text-3xl tracking-tighter mb-3">Built in public, for the public.</h3>
          <p className="text-zinc-300 mb-6 leading-relaxed">
            We&apos;re building TAXINPHO out in the open — feature by feature, request by
            request. If you&apos;re a CA, advocate, or tax practitioner, drop us a note.
            We&apos;d love to hear what would make your day easier.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/contact" className="border border-zinc-700 text-white px-5 py-2.5 text-sm font-semibold hover:bg-zinc-900">
              Get in touch
            </Link>
            <Link to="/register" className="bg-[#1ABEDC] text-zinc-950 px-5 py-2.5 text-sm font-semibold flex items-center gap-1.5 hover:bg-cyan-400">
              Start free trial <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
